import groupBy from "lodash-es/groupBy.js";
import flatMap from "lodash-es/flatMap.js";
import { getGoliathImage } from "../goliath-media.js";
export var ProductSelectorFieldWithPrice;
(function (ProductSelectorFieldWithPrice) {
    ProductSelectorFieldWithPrice["Color"] = "COLOR";
    ProductSelectorFieldWithPrice["Size"] = "SIZE";
    ProductSelectorFieldWithPrice["Storage"] = "STORAGE";
    ProductSelectorFieldWithPrice["Price"] = "PRICE";
})(ProductSelectorFieldWithPrice || (ProductSelectorFieldWithPrice = {}));
const PROPERTY_KEY_MAP = {
    COLOR: "Farbe",
    STORAGE: "Speichergröße",
    SIZE: "Größe",
    PRICE: "Preis",
};
const mapName = (productAlternative, type) => {
    // Ensure productAlternative.offer exists
    if (!productAlternative.offer?.scopedProduct) {
        return "";
    }
    const scopedProduct = productAlternative.offer.scopedProduct;
    const fallbackName = scopedProduct.variantName || scopedProduct.name || "";
    switch (type) {
        case "SIZE":
            return scopedProduct.size || fallbackName;
        case "COLOR":
            return scopedProduct.color?.name || fallbackName;
        case "STORAGE":
            return scopedProduct.storage
                ? `${scopedProduct.storage.value} ${scopedProduct.storage.unit}`
                : fallbackName;
        case "PRICE":
            return getAlternativeWithOnetimePriceOnly([productAlternative])?.length >
                0
                ? "Kaufen"
                : "Mieten";
        default:
            return scopedProduct.variantName || "";
    }
};
/**
 * maps a `productAlternative` from offer request to needed values
 */
const mapProductAlternative = (productAlternative, type, detailViewPath, generateSlug = undefined) => {
    if (!productAlternative.offer)
        return undefined;
    const { product, scopedProduct } = productAlternative.offer;
    const path = generateSlug
        ? generateSlug(product?.slug || "")
        : detailViewPath + product?.slug;
    return {
        offer: productAlternative.offer,
        exactMatch: productAlternative.exactMatch || false,
        active: productAlternative.active || false,
        name: mapName(productAlternative, type),
        path,
        color: scopedProduct?.color || null,
        size: null,
        storage: null,
        teaserImage: getGoliathImage(productAlternative.offer),
    };
};
export const sortVariants = (variants, type) => {
    if (type === "SIZE") {
        const sortingArray = [
            "size xs",
            "size s",
            "size m",
            "size l",
            "size xl",
            "size xxl",
        ];
        variants.sort((a, b) => sortingArray.indexOf(a.name?.toLowerCase() || "") -
            sortingArray.indexOf(b.name?.toLowerCase() || ""));
        return variants;
    }
    return variants;
};
/**
 * maps a `ProductAlternativeSelectorGroup` from offer request to needed values
 */
const mapSelectorGroup = (selectorGroup, detailViewPath, generateSlug = undefined) => {
    const variants = [];
    selectorGroup.alternatives.forEach((alternative) => {
        const transformedAlternative = mapProductAlternative(alternative, selectorGroup.key, detailViewPath, generateSlug);
        if (!transformedAlternative)
            return;
        variants.push(transformedAlternative);
    });
    return {
        type: selectorGroup.key,
        displayName: PROPERTY_KEY_MAP[selectorGroup.key],
        items: sortVariants(variants, selectorGroup.key),
    };
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createAdditionalGroupForTesting = (selectorGroup, detailViewPath, generateSlug = undefined) => ({
    type: "STORAGE",
    displayName: PROPERTY_KEY_MAP.STORAGE,
    items: selectorGroup.alternatives
        .map((alternative) => mapProductAlternative(alternative, "STORAGE", detailViewPath, generateSlug))
        .filter(Boolean),
});
/**
 * determine if a bundleItem is configurable by looking for non active alternatives
 */
const isConfigurable = (properties) => flatMap(properties, ({ items }) => items).filter(({ active }) => !active)
    .length > 0;
/**
 * returns the variantNames of the active variant e.g `['schwarz','256 GB']`
 */
const getActiveVariants = (properties) => flatMap(properties, (p) => p.items)
    .filter((a) => a.active)
    .map((a) => a.name);
const getTeaserImages = (properties) => {
    const activeTeaserImages = [];
    const otherTeaserImages = [];
    const mediaIds = new Set();
    properties.forEach((property) => {
        property.items.forEach((variant) => {
            if (variant.teaserImage && !mediaIds.has(variant.teaserImage.mediaId)) {
                mediaIds.add(variant.teaserImage.mediaId);
                if (variant.active) {
                    activeTeaserImages.push(variant.teaserImage);
                }
                else {
                    otherTeaserImages.push(variant.teaserImage);
                }
            }
        });
    });
    // Combine active teaser images with other teaser images
    return [...activeTeaserImages, ...otherTeaserImages];
};
/**
 * Offer with Monthly Price may have a ONETIME price as well
 */
const getAlternativeWithMonthlyPrice = (alternatives) => alternatives.filter(({ offer }) => !!offer?.prices?.find((price) => price?.paymentFrequency === "MONTHLY" && price?.type === "DEFAULT"));
/**
 * Offers with onetime price must not have a monthly price
 */
const getAlternativeWithOnetimePriceOnly = (alternatives) => alternatives.filter(({ offer }) => {
    const defaultPrices = offer?.prices?.filter((price) => price?.type === "DEFAULT") || [];
    if (defaultPrices.length === 0)
        return false;
    return defaultPrices.every((price) => price?.paymentFrequency === "ONETIME");
});
/**
 * Search alternative with same props (size, storage). ignore Color, because it is the "default" from goliath.
 */
const findProductByVariant = (offer, alternatives) => {
    return alternatives.find((alternative) => {
        return (alternative.offer?.scopedProduct?.size === offer?.size &&
            alternative.offer?.scopedProduct?.storage?.unit === offer?.storage?.unit);
    });
};
/**
 * This will extract variants with different prices (onetime, monthly) as separate alternative
 */
const extractDifferentPrices = (properties) => {
    const filteredProperties = [];
    properties.forEach((props) => {
        if (!props || !props.key)
            return undefined;
        // Use a type guard to filter out null or undefined values
        const validAlternatives = (props?.alternatives || []).filter((alternative) => alternative !== null && alternative !== undefined);
        const recurring = getAlternativeWithMonthlyPrice(validAlternatives);
        const onetime = getAlternativeWithOnetimePriceOnly(validAlternatives);
        if (onetime.length === 0 && recurring.length === 0) {
            filteredProperties.push(props);
            return;
        }
        if (onetime.length > 0) {
            filteredProperties.push({
                key: props.key,
                alternatives: onetime,
            });
        }
        if (recurring.length === 0)
            return;
        // Monthly or onetime price has to be active.
        const activeRecurring = recurring.find((e) => e.active);
        if (activeRecurring && "offer" in activeRecurring) {
            const matchingAlternative = findProductByVariant(activeRecurring.offer?.scopedProduct, onetime);
            if (!matchingAlternative)
                return;
            filteredProperties.push({
                key: "PRICE",
                alternatives: [matchingAlternative, activeRecurring],
            });
            return;
        }
        const activeOnetime = onetime.find((e) => e.active);
        if (activeOnetime && activeOnetime && "offer" in activeOnetime) {
            const matchingAlternative = findProductByVariant(activeOnetime.offer?.scopedProduct, recurring);
            if (!matchingAlternative)
                return;
            filteredProperties.push({
                key: ProductSelectorFieldWithPrice.Price,
                alternatives: [activeOnetime, matchingAlternative],
            });
        }
    });
    return filteredProperties;
};
/**
 * maps each bundleItem to needed values for component
 */
function mapToReducedBundleItems(bundleItem, detailViewPath, generateSlug = undefined) {
    if (!Array.isArray(bundleItem.properties))
        return undefined;
    const filteredProperties = extractDifferentPrices(bundleItem.properties).filter(Boolean);
    const properties = [
        ...filteredProperties.map((property) => mapSelectorGroup(property, detailViewPath, generateSlug)),
        // ...filteredProperties.map(createAdditionalGroupForTesting), // ==> enable for testing
    ];
    return {
        family: bundleItem.family,
        position: bundleItem.position || 0,
        isConfigurable: isConfigurable(properties),
        activeVariants: getActiveVariants(properties),
        images: getTeaserImages(properties),
        properties,
        variants: {
            COLOR: properties.find((p) => p.type === "COLOR"),
            STORAGE: properties.find((p) => p.type === "STORAGE"),
            SIZE: properties.find((p) => p.type === "SIZE"),
            PRICE: properties.find((p) => p.type === "PRICE"),
        },
    };
}
/**
 * generate a name for merged bundleItems
 */
const itemNameWithCount = (name, count) => count > 1 ? `${count} x ${name}` : name;
/**
 * merges duplicate non-configurable bundleItems into a single item
 */
const maybeMergeBundleItems = (items) => {
    const mergeableItems = items.filter((item) => !item.isConfigurable);
    const nonMergeableItems = items.filter((item) => item.isConfigurable);
    const groups = groupBy(mergeableItems, "family.name");
    const groupsArray = Object.keys(groups).map((g) => groups[g]);
    const mergedItems = groupsArray.map((group) => ({
        ...group[0],
        isMerged: group.length > 1,
        family: {
            ...group[0].family,
            name: itemNameWithCount(group[0].family?.name || "", group.length),
        },
    }));
    return [...mergedItems, ...nonMergeableItems];
};
/**
 * Takes a Goliath Offer and transforms the productAlternatives into alternativeBundleItems for the OfferConfigurator component
 */
export const transformOfferAlternatives = (productAlternatives, detailViewPath = "/geraete/", generateSlug = undefined) => {
    const items = productAlternatives
        .map((item) => mapToReducedBundleItems(item, detailViewPath, generateSlug))
        .filter((item) => Boolean(item));
    return maybeMergeBundleItems(items);
};
export const getFirstOfferAlternatives = (productAlternatives, detailViewPath = "/geraete/", generateSlug = undefined) => {
    return transformOfferAlternatives(productAlternatives, detailViewPath, generateSlug)[0];
};
